import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import Tabs from "../../Components/Tabs";
import { getAssistantById } from "../../actions/assistants";
import { CheckOnlyIcon, ArrowIcon, PersonIcon, RobotIcon } from "../Auth/onboarding/icons";
import { SearchIcon, LinkIcon, CalendarIcon } from "../Dashboard/icons";
import { HeadsetIcon } from "./icons";
import { integrations, categories } from "./data";
import {
  getIntegrationAuthorizeUrl,
  disconnectIntegration,
} from "../../actions/integrations";

// Each category gets a glyph so the chip reads at a glance, not just as text.
const CATEGORY_ICONS = {
  Scheduling: <CalendarIcon />,
  CRM: <PersonIcon />,
  Helpdesk: <HeadsetIcon />,
};

// The provider slug the connect endpoint expects, keyed by the field name the
// assistant record uses. A key missing here has no OAuth route yet.
const CONNECT_SLUGS = {
  calendly: "calendly",
  googleCalendar: "google-calendar",
};

// How the backend reports the outcome when it redirects back here: the query
// param it sets, the human name for the message, and the field on the
// assistant record that proves the connection.
//
// Several spellings per provider on purpose - the redirect URL is the
// backend's to change, and a rename there used to turn every successful
// connection into "please try again".
const RESULT_PARAMS = {
  calendly: { label: "Calendly", field: "calendly" },
  google: { label: "Google Calendar", field: "googleCalendar" },
  googleCalendar: { label: "Google Calendar", field: "googleCalendar" },
  "google-calendar": { label: "Google Calendar", field: "googleCalendar" },
  google_calendar: { label: "Google Calendar", field: "googleCalendar" },
};

// Anything the backend might call success. Only consulted when the record
// can't be read - it is the fallback, not the verdict.
const SUCCESS_VALUES = ["connected", "success", "true", "ok", "1", "done"];

export default function Integrations() {
  const [params, setParams] = useSearchParams();
  const dispatch = useDispatch();
  const assistantId = useSelector((state) => state.assistants.activeId);

  // The `integrations` map off GET /assistants/:id. null while it loads, so a
  // tile can't briefly claim "Coming soon" before the answer arrives.
  const [connections, setConnections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  // Set while the browser is on its way to the provider, so the button can't
  // be fired twice during the round trip.
  const [starting, setStarting] = useState("");
  const [disconnecting, setDisconnecting] = useState("");

  const load = useCallback(async () => {
    if (!assistantId) return;

    setLoading(true);
    const result = await dispatch(getAssistantById(assistantId));
    setLoading(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't load your integrations.");
      return;
    }

    setConnections(result.assistant?.integrations || {});
  }, [assistantId, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // The backend finishes the handshake and redirects back here with e.g.
  // ?google=connected. The param is stripped afterwards so a refresh doesn't
  // replay a stale message.
  //
  // The record, not the param, decides the outcome: re-reading the assistant
  // says whether the integration is actually stored, which survives the
  // backend renaming its redirect params or changing what it calls success.
  useEffect(() => {
    const hit = Object.keys(RESULT_PARAMS).find((key) => params.get(key));
    if (!hit) return;

    const { label, field } = RESULT_PARAMS[hit];
    const outcome = String(params.get(hit) || "").toLowerCase();
    // Some providers hand back a reason worth showing.
    const reason = params.get("message") || params.get("error") || "";

    // Strip the params first, so this can't re-run against itself.
    const next = new URLSearchParams(params);
    next.delete(hit);
    next.delete("message");
    next.delete("error");
    setParams(next, { replace: true });

    const report = (connected) => {
      if (connected) {
        setNotice(`${label} is connected.`);
        setError("");
        return;
      }

      setNotice("");
      setError(
        reason
          ? `We couldn't connect ${label}: ${reason}`
          : `We couldn't connect ${label}. Please try again.`,
      );
    };

    if (!assistantId) {
      report(SUCCESS_VALUES.includes(outcome));
      return;
    }

    dispatch(getAssistantById(assistantId)).then((result) => {
      if (!result?.success) {
        report(SUCCESS_VALUES.includes(outcome));
        return;
      }

      const integrations = result.assistant?.integrations || {};
      setConnections(integrations);
      report(Boolean(integrations[field]));
    });
  }, [params, setParams, assistantId, dispatch]);

  const visible = useMemo(() => {
    const search = query.trim().toLowerCase();

    return integrations.filter((item) => {
      if (category !== "All" && item.category !== category) return false;
      if (!search) return true;

      return (
        item.name.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search) ||
        item.description.toLowerCase().includes(search)
      );
    });
  }, [query, category]);

  const handleConnect = async (item) => {
    setNotice("");
    setError("");

    const provider = CONNECT_SLUGS[item.apiKey];

    if (!provider) {
      setError(`${item.name} can't be connected from here yet.`);
      return;
    }

    setStarting(item.id);

    // The backend builds the authorize URL — client id, its own redirect URI
    // and a signed state — so nothing about the OAuth app lives in this app.
    const result = await dispatch(
      getIntegrationAuthorizeUrl(assistantId, provider),
    );

    if (!result?.success) {
      setStarting("");
      setError(result?.message || `We couldn't start the ${item.name} connection.`);
      return;
    }

    // Leaves the SPA entirely, so `starting` is never cleared on this path.
    window.location.assign(result.authorizeUrl);
  };

  const handleDisconnect = async (item) => {
    setNotice("");
    setError("");

    const provider = CONNECT_SLUGS[item.apiKey];
    if (!provider) return;

    setDisconnecting(item.id);

    const result = await dispatch(
      disconnectIntegration(assistantId, provider),
    );

    setDisconnecting("");

    if (!result?.success) {
      setError(result?.message || `We couldn't disconnect ${item.name}.`);
      return;
    }

    setNotice(`${item.name} was disconnected.`);
    // Re-read rather than flipping a local flag, so the tiles keep matching
    // what the server actually stores.
    await load();
  };

  const renderTile = (item) => {
    // A key absent from the map is one the backend doesn't implement yet.
    const supported = Boolean(connections) && item.apiKey in connections;
    const connected = connections?.[item.apiKey] === true;
    const comingSoon = Boolean(connections) && !supported;

    return (
      <div
        key={item.id}
        className={`intg-tile${connected ? " is-connected" : ""}${comingSoon ? " is-soon" : ""}`}
      >
        {connected && (
          <span className="intg-tile__badge">
            <CheckOnlyIcon />
            Connected
          </span>
        )}
        {comingSoon && (
          <span className="intg-tile__badge intg-tile__badge--soon">
            Coming soon
          </span>
        )}

        <span className="intg-tile__logo">{item.logo}</span>

        <span className="intg-tile__cat">
          {CATEGORY_ICONS[item.category]}
          {item.category}
        </span>

        <span className="intg-tile__label">{item.name}</span>

        <span className="intg-tile__desc">{item.description}</span>

        <button
          type="button"
          className={`intg-tile__btn${connected ? " is-connected" : ""}`}
          onClick={() =>
            connected ? handleDisconnect(item) : handleConnect(item)
          }
          disabled={
            loading ||
            comingSoon ||
            Boolean(starting) ||
            disconnecting === item.id
          }
        >
          {!comingSoon && !connected && <LinkIcon />}
          <span>
            {comingSoon
              ? "Coming soon"
              : connected
                ? disconnecting === item.id
                  ? "Disconnecting…"
                  : "Disconnect"
                : starting === item.id
                  ? "Redirecting…"
                  : loading
                    ? "Checking…"
                    : "Connect"}
          </span>
          {!comingSoon && !connected && (
            <span className="intg-tile__btn-arrow" aria-hidden="true">
              <ArrowIcon />
            </span>
          )}
        </button>
      </div>
    );
  };

  const renderBody = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    if (visible.length === 0) {
      return (
        <section className="dash-card intg-empty">
          <h2 className="cz-card-title">No integrations found</h2>
          <p className="ist-card-subtitle">
            {query.trim()
              ? `Nothing matches "${query.trim()}"${category === "All" ? "" : ` in ${category}`}.`
              : `Nothing in ${category} yet.`}
          </p>
          <button
            type="button"
            className="onboarding__btn onboarding__btn--primary"
            onClick={() => {
              setQuery("");
              setCategory("All");
            }}
          >
            Show all integrations
          </button>
        </section>
      );
    }

    return <div className="intg-tiles">{visible.map(renderTile)}</div>;
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title="Integrations"
          subtitle="Connect the tools your team already works in - calendars for booking, CRM and support for everything your assistant captures."
        >
          <label className="intg-search">
            <SearchIcon />
            <input
              type="search"
              value={query}
              placeholder="Search integrations"
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search integrations"
            />
          </label>

          {/* <div className="intg-head-links">
            <a href="#guides" className="intg-head-link">
              View our Integration Guides
            </a>
            <span className="intg-head-divider" aria-hidden="true">
              |
            </span>
            <a href="#request" className="intg-head-link">
              Request a New Integration
            </a>
          </div> */}
        </Topbar>

        <div className="dash-content">
          <Tabs tabs={categories} active={category} onChange={setCategory} />

          {notice && <p className="auth-success intg-notice">{notice}</p>}
          {error && <p className="auth-error intg-notice">{error}</p>}

          {renderBody()}
        </div>
      </div>
    </div>
  );
}
